import { Instagram, MapPin, Phone, MessageCircle, Clock, Car, MapPinned, Wrench, Mail, FileText, ExternalLink } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import { BRAND, PHONES, buildWhatsAppLink, buildTelLink } from "@/lib/constants";

const contacts = [
    {
        Icon: Car,
        title: "Aluguel de Carros",
        phone: PHONES.aluguel,
        message: "Olá! Tenho interesse no aluguel de veículos da PEG.",
        key: "aluguel",
    },
    {
        Icon: MapPinned,
        title: "Rastreamento Veicular",
        phone: PHONES.rastreamento,
        message: "Olá! Quero saber mais sobre o rastreamento veicular.",
        key: "rastreamento",
    },
    {
        Icon: Wrench,
        title: "Guincho 24h",
        phone: PHONES.guincho,
        message: "Olá! Preciso de um guincho com urgência.",
        key: "guincho",
    },
];

export default function Contato() {
    return (
        <div data-testid="contato-page">
            {/* Hero */}
            <section className="bg-[#1a1a1a] text-white py-20 md:py-24 relative overflow-hidden">
                <div className="absolute -top-32 -right-20 w-[400px] h-[400px] bg-[#fec200] opacity-15 blur-[120px] rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#fec200] mb-4">
                        Contato
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                        Fale com a <span className="text-[#fec200]">PEG Serviços</span>
                    </h1>
                    <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
                        Estamos prontos para atender você. Escolha o serviço que precisa e fale diretamente com nossa equipe via WhatsApp ou telefone.
                    </p>
                </div>
            </section>

            {/* Contact cards */}
            <section className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-6">
                        {contacts.map(({ Icon, title, phone, message, key }) => (
                            <div
                                key={key}
                                data-testid={`contato-card-${key}`}
                                className="bg-white p-8 border-t-4 border-[#fec200] hover:shadow-2xl transition-shadow flex flex-col"
                            >
                                <div className="w-14 h-14 bg-[#fec200] flex items-center justify-center">
                                    <Icon className="w-7 h-7 text-[#1a1a1a]" />
                                </div>
                                <h3 className="font-display text-2xl font-black mt-5">{title}</h3>
                                <p className="text-sm text-[#4a4a4a] mt-2">Atendimento direto</p>

                                <div className="mt-6 space-y-3">
                                    <a
                                        href={buildTelLink(phone.raw)}
                                        data-testid={`contato-call-${key}`}
                                        className="flex items-center gap-3 bg-[#1a1a1a] text-white p-4 hover:bg-[#fec200] hover:text-[#1a1a1a] transition-colors"
                                    >
                                        <Phone className="w-5 h-5 text-[#fec200] group-hover:text-[#1a1a1a]" />
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest font-bold">Telefone</p>
                                            <p className="font-bold">{phone.display}</p>
                                        </div>
                                    </a>
                                    <a
                                        href={buildWhatsAppLink(phone.raw, message)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`contato-whatsapp-${key}`}
                                        className="flex items-center gap-3 bg-[#25D366] text-white p-4 hover:opacity-90 transition-opacity"
                                    >
                                        <MessageCircle className="w-5 h-5" />
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest font-bold">WhatsApp</p>
                                            <p className="font-bold">Iniciar conversa</p>
                                        </div>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Info strip */}
            <section className="bg-white py-20 md:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#fec200] flex items-center justify-center flex-shrink-0">
                            <Clock className="w-6 h-6 text-[#1a1a1a]" />
                        </div>
                        <div>
                            <h4 className="font-display font-black text-xl">Horário de atendimento</h4>
                            <p className="text-[#4a4a4a] mt-1">Guincho 24h: todos os dias, 24 horas</p>
                            <p className="text-[#4a4a4a]">Demais serviços: Seg a Sáb, 08h às 18h</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#fec200] flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-6 h-6 text-[#1a1a1a]" />
                        </div>
                        <div>
                            <h4 className="font-display font-black text-xl">Área de atendimento</h4>
                            <p className="text-[#4a4a4a] mt-1">Cobertura em toda a {BRAND.region}.</p>
                            <p className="text-[#4a4a4a]">Consulte disponibilidade para sua região.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#fec200] flex items-center justify-center flex-shrink-0">
                            <Instagram className="w-6 h-6 text-[#1a1a1a]" />
                        </div>
                        <div>
                            <h4 className="font-display font-black text-xl">Redes sociais</h4>
                            <p className="text-[#4a4a4a] mt-1">Acompanhe nossos lançamentos e dicas.</p>
                            <a
                                href={BRAND.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contato-instagram"
                                className="text-[#1a1a1a] font-bold border-b-2 border-[#fec200] inline-block mt-1"
                            >
                                {BRAND.instagramHandle}
                            </a>
                        </div>
                    </div>
                </div>
            </section>
            {/* Map + endereço */}
            <section data-testid="contato-mapa" className="bg-[#f5f5f5] pb-20 md:pb-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Onde estamos"
                        title="Nossa sede em Bom Jesus da Lapa"
                        subtitle="Venha nos visitar ou veja o ponto de partida das nossas operações em toda a Bahia."
                    />

                    <div className="mt-12 grid lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-7 relative">
                            <div className="absolute -inset-2 bg-[#fec200]" />
                            <div className="relative aspect-[16/10] bg-white">
                                <iframe
                                    title="Mapa PEG Serviços"
                                    src={BRAND.address.embedUrl}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    data-testid="contato-map-iframe"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-5 bg-[#1a1a1a] text-white p-8 md:p-10 flex flex-col">
                            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#fec200]">
                                Sede PEG
                            </p>
                            <h3 className="font-display text-3xl font-black mt-3 leading-tight">
                                Av. Zeca Miranda, 254
                            </h3>
                            <p className="text-white/70 mt-2 text-lg">
                                Lagoa Grande, Bom Jesus da Lapa/BA
                            </p>
                            <p className="text-white/60 text-sm mt-1">CEP 47.600-000</p>

                            <div className="space-y-3 mt-8">
                                <a
                                    href={`mailto:${BRAND.email}`}
                                    data-testid="contato-email"
                                    className="flex items-center gap-3 bg-white/5 border border-white/10 hover:border-[#fec200] p-4 transition-colors"
                                >
                                    <Mail className="w-5 h-5 text-[#fec200]" />
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#fec200]">E-mail</p>
                                        <p className="font-bold">{BRAND.email}</p>
                                    </div>
                                </a>
                                <div className="flex items-center gap-3 bg-white/5 border border-white/10 p-4" data-testid="contato-cnpj">
                                    <FileText className="w-5 h-5 text-[#fec200]" />
                                    <div>
                                        <p className="text-[10px] uppercase tracking-widest font-bold text-[#fec200]">CNPJ</p>
                                        <p className="font-bold">{BRAND.cnpj}</p>
                                    </div>
                                </div>
                            </div>

                            <a
                                href={BRAND.address.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contato-maps-link"
                                className="mt-auto pt-6 inline-flex items-center justify-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-6 py-4 hover:bg-white transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Abrir no Google Maps
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
